import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { StarRating } from "@/components/StarRating";
import { PairingRecipes } from "@/components/RecipeBlock";
import { ReelLoader } from "@/components/ReelLoader";
import { MovieNightMode } from "@/components/MovieNightMode";
import { ShoppingList } from "@/components/ShoppingList";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Globe, Lock, Trash2, Share2, Clapperboard, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

export default function PairingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pairing, setPairing] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [nightMode, setNightMode] = useState(false);
  const [shopping, setShopping] = useState(false);

  useEffect(() => {
    api
      .get(`/pairings/${id}`)
      .then((res) => setPairing(res.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const rate = async (n) => {
    try {
      const { data } = await api.patch(`/pairings/${id}/rate`, { rating: n });
      setPairing(data);
      toast.success(`Rated ${n} star${n > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleVisibility = async (val) => {
    try {
      const { data } = await api.patch(`/pairings/${id}/visibility`, { is_public: val });
      setPairing(data);
      toast.success(val ? "Now public" : "Now private");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/pairings/${id}`);
      toast.success("Pairing deleted");
      navigate("/pairings");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/p/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard!");
    } catch {
      toast.message(url);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen pt-32 text-center px-5" data-testid="pairing-not-found">
        <h1 className="font-display text-3xl font-bold mb-3">Pairing not found</h1>
        <Button onClick={() => navigate("/pairings")} className="rounded-full mt-4">
          Back to My Pairings
        </Button>
      </div>
    );
  }

  if (!pairing) {
    return (
      <div className="min-h-screen pt-24">
        <ReelLoader label="Loading pairing..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-5 sm:px-8" data-testid="pairing-detail-page">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate("/pairings")}
          data-testid="detail-back-btn"
          className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back to My Pairings
        </button>

        <div className="animate-fade-up">
          <span className="uppercase text-xs tracking-[0.3em] text-[#E50914]">
            Movie Pairing
          </span>
          <h1 className="font-display text-4xl sm:text-6xl font-black mt-2 mb-4">
            {pairing.movie_title}
          </h1>
          {pairing.theme_description && (
            <p className="text-lg font-light leading-relaxed text-white/85 mb-6 max-w-2xl">
              {pairing.theme_description}
            </p>
          )}

          <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/40 mb-2">
                Your Rating
              </p>
              <StarRating value={pairing.rating} onRate={rate} size={26} testid="detail-rating" />
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2.5">
                {pairing.is_public ? (
                  <Globe className="w-5 h-5 text-[#5EC5A0]" />
                ) : (
                  <Lock className="w-5 h-5 text-white/40" />
                )}
                <span className="text-sm text-white/70">
                  {pairing.is_public ? "Public" : "Private"}
                </span>
                <Switch
                  checked={pairing.is_public}
                  onCheckedChange={toggleVisibility}
                  data-testid="detail-visibility-toggle"
                />
              </div>

              {pairing.is_public && (
                <Button
                  variant="ghost"
                  onClick={share}
                  data-testid="detail-share-btn"
                  className="rounded-full bg-white/5 hover:bg-white/10 text-white h-10 px-4"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
              )}

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    data-testid="detail-delete-btn"
                    className="rounded-full bg-white/5 hover:bg-[#E50914]/20 text-[#E50914] h-10 w-10 p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#0d0d0d] border-white/10" data-testid="delete-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-display">
                      Delete this pairing?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-white/60">
                      This will permanently remove the "{pairing.movie_title}" pairing. This
                      action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel
                      data-testid="delete-cancel-btn"
                      className="rounded-full bg-white/5 border-white/10 hover:bg-white/10"
                    >
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={remove}
                      data-testid="delete-confirm-btn"
                      className="rounded-full bg-[#E50914] hover:bg-[#c00811]"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          <div className="mb-6 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => setNightMode(true)}
              data-testid="movie-night-btn"
              className="w-full sm:w-auto rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white h-12 px-8"
            >
              <Clapperboard className="w-5 h-5 mr-2 text-[#E50914]" />
              Start Movie Night Mode
            </Button>
            <Button
              onClick={() => setShopping(true)}
              data-testid="shopping-list-btn"
              className="w-full sm:w-auto rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white h-12 px-8"
            >
              <ShoppingCart className="w-5 h-5 mr-2 text-[#5EC5A0]" />
              Shopping List
            </Button>
          </div>

          <PairingRecipes pairing={pairing} />
        </div>
      </div>

      <MovieNightMode
        pairing={pairing}
        open={nightMode}
        onClose={() => setNightMode(false)}
      />
      <ShoppingList
        pairing={pairing}
        open={shopping}
        onClose={() => setShopping(false)}
      />
    </div>
  );
}
